import { z } from 'zod';
import {
    GeoJsonFeatureTypes,
    GeoJsonTypes,
    MapStylesFillOpacity,
    MapStylesStrokeColor,
    MapStylesStrokeWeight,
    MapStylesZIndex,
} from './mc-map-enum';
import { InjectionToken } from '@angular/core';

export const defaultDatiqMapStyleConfiguration: IDatiqMapStyleConfiguration = {
    clickable: true,
    fillColor: '#f5f5f5', // White No Data
    fillOpacity: MapStylesFillOpacity.Default,
    strokeColor: MapStylesStrokeColor.Default,
    strokeWeight: MapStylesStrokeWeight.Default,
    zIndex: MapStylesZIndex.Default,
};

export const emptyDatIqMapTooltip: IDatiqMapTooltip = {
    title: '',
    subtitle: '',
    show: false,
};

export const featureSelectedStyleOptions: google.maps.Data.StyleOptions = {
    strokeWeight: MapStylesStrokeWeight.Selected,
    fillOpacity: MapStylesFillOpacity.Selected,
    zIndex: MapStylesZIndex.Selected,
};

export interface IIqMapGaugeLegend {
    range: string[];
    colors: string[];
}
export interface IDatiqMapGeoJson {
    type: GeoJsonTypes;
    name: string;
    features: IDatiqMapGeoJsonFeature[];
}

export interface IDatiqMapStyleConfiguration {
    clickable?: boolean;
    fillColor?: string;
    fillOpacity?: number;
    strokeColor?: string;
    strokeWeight?: number;
    zIndex?: number;
}

export interface IIqMcMapGeoJsonFeaturePropertiesStyles {
    fillColor?: string;
    fillOpacity?: number;
    strokeColor?: string;
    strokeWeight?: number;
    clickable?: boolean;
    zIndex?: number;
}

export interface IIqMapGeoJsonFeatureProperties {
    code: string;
    name: string;
    style?: IIqMcMapGeoJsonFeaturePropertiesStyles;
    data?: any;
    tooltip?: IDatiqMapTooltip;
}

export interface IDatiqMapGeoJsonFeature {
    type: GeoJsonFeatureTypes;
    properties: IDatiqMapGeoJsonFeatureProperties;
    geometry: any;
    id?: string;
}

export interface IDatiqMapGeoJsonFeatureProperties {
    code: string;
    name: string;
    style?: IDatiqMapGeoJsonFeaturePropertiesStyles;
    data?: any;
    tooltip?: IDatiqMapTooltip;
}

export interface IDatiqMapGeoJsonFeaturePropertiesStyles {
    fillColor?: string;
    fillOpacity?: number;
    strokeColor?: string;
    strokeWeight?: number;
    clickable?: boolean;
    zIndex?: number;
}

export interface IDatiqMapTooltip {
    title: string;
    subtitle: string;
    show: boolean;
}

export interface IDatiqMapMetricCellLabel {
    text: string;
    color?: string;
    bg?: string;
}
export interface IDatiqMapRange {
    low: number;
    high: number;
}
export interface IDatiqMapMetricCell {
    color: string;
    range: IDatiqMapRange;
    label?: IDatiqMapMetricCellLabel;
    data?: any;
    index: number;
}

export interface IDatiqMapMetric {
    id: string;
    label: string;
    range: IDatiqMapRange;
    styles: IDatiqMapMetricCell[];
}

export interface IIqMcMapLatLng {
    lat: number;
    lng: number;
}

export const McMciDataNodeSchema = z.object({
    geoCode: z.union([z.string(), z.number()]),
    mci: z.number(),
    label: z.string(),
    color: z.string().nullable(),
    capacity: z.string().nullable(),
});

export const McMciDataArraySchema = z.array(McMciDataNodeSchema);

export type McMciDataNode = z.infer<typeof McMciDataNodeSchema>;
export type McMciDataArray = z.infer<typeof McMciDataArraySchema>;

const DirectionSchema = z.enum(['IN', 'OUT']);

export type Direction = z.infer<typeof DirectionSchema>;

const TimeframeSchema = z.enum([
    'THIRTY_DAYS_BACK',
    'EIGHT_DAYS_BACK',
    'PREV_BUSINESS_DAY',
    'CURRENT',
    'EIGHT_DAY_FORECAST',
    '',
]);

export type Timeframe = z.infer<typeof TimeframeSchema>;

const EquipmentTypeSchema = z.enum(['VAN', 'REEFER', 'FLATBED']);

const GeoTypesSchema = z.enum([
    'COUNTRY',
    'REGION',
    'STATE',
    'X_MKT',
    'MKT',
    'THREEDZ',
]);

export type GeoTypes = z.infer<typeof GeoTypesSchema>;

export type EquipmentType = z.infer<typeof EquipmentTypeSchema>;

const LtRatioPayloadSchema = z
    .array(
        z.object({
            geoCode: z.string().or(z.number()),
            ratio: z.number().nullable(),
        }),
    )
    .default([]);

export type LtRatio = { geoCode?: string | number; ratio?: number };

export type LtRatioPayload = z.infer<typeof LtRatioPayloadSchema>;

export const LtMapDataPayloadSchema = z.object({
    id: z.string(),
    dashboardId: z.string(),
    direction: DirectionSchema,
    geoType: GeoTypesSchema,
    geoCode: z.string().or(z.null()).or(z.number()).default(''),
    equipmentType: EquipmentTypeSchema.default('VAN'),
    timeframe: TimeframeSchema.default('CURRENT'),
    displayName: z.string().or(z.null()).default(''),
    daysForward: z.number().default(0),
    hasError: z.boolean().default(false),
    loaded: z.boolean().default(false),
    ratioMap: LtRatioPayloadSchema,
});

export type LtMapDataPayload = z.infer<typeof LtMapDataPayloadSchema>;

export type AreaPayload = {
    id: string;
    loaded: boolean;
    featureCollection: IDatiqMapGeoJson;
    name: string;
    hasError: boolean;
};

export type MciRatio = {
    geoCode?: string | number;
    mci?: number;
    capacity?: string;
    capacityColor?: string;
};

const MciRatioPayloadSchema = z.array(
    z.object({
        geoCode: z.string().or(z.number()).nullable(),
        mci: z.number().nullable(),
        capacity: z.string().nullable(),
        capacityColor: z.string().nullable(),
    }),
);

export type MciRatioPayload = z.infer<typeof MciRatioPayloadSchema>;

export const MciMapDataPayloadSchema = z.object({
    id: z.string(),
    dashboardId: z.string(),
    direction: DirectionSchema,
    geoType: GeoTypesSchema,
    geoCode: z.string().or(z.null()).or(z.number()).default(''),
    equipmentType: EquipmentTypeSchema.default('VAN'),
    timeframe: TimeframeSchema.default('CURRENT'),
    displayName: z.string().or(z.null()).default(''),
    daysForward: z.number().default(0),
    hasError: z.boolean().default(false),
    loaded: z.boolean().default(false),
    mciScores: MciRatioPayloadSchema,
});

export type MciMapDataPayload = z.infer<typeof MciMapDataPayloadSchema>;

export const McQuerySchema = z.object({
    id: z.string(),
    type: z.literal('area'),
    direction: z.union([z.literal('IN'), z.literal('OUT')]),
    geoType: GeoTypesSchema,
    geoCode: z.string().default(''),
    equipmentType: EquipmentTypeSchema,
    timeframe: TimeframeSchema.default('CURRENT'),
    displayName: z.string().or(z.null()).default(''),
    daysForward: z.string().or(z.null()).default(''),
    forecastDate: z.string().or(z.null()).default(''),
});

export type McQuery = z.infer<typeof McQuerySchema>;

export const ErrorSchema = z.object({
    title: z.string().optional().default(''),
    subtitle: z.string().optional().default(''),
});

export type Error = z.infer<typeof ErrorSchema>;

//#region Default Map Data Payloads

const PartialDefaultLtMapPayload: Partial<LtMapDataPayload> = {
    id: '',
    dashboardId: '',
    direction: 'IN',
    geoType: 'COUNTRY',
    geoCode: '',
    equipmentType: 'VAN',
    ratioMap: [],
    loaded: true,
    displayName: '',
    daysForward: 0,
}

export const LtMapDataPayloadError: LtMapDataPayload = {
    ...PartialDefaultLtMapPayload,
    timeframe: 'CURRENT',
    hasError: true,
}

export const LtMapDataPayloadForecastError: LtMapDataPayload = {
    ...PartialDefaultLtMapPayload,
    timeframe: 'EIGHT_DAY_FORECAST',
    hasError: true,
}

export const DefaultLtError: Error = {
    title: 'Load/Truck Ratio is not available',
    subtitle: 'Please try again later',
}

export const DefaultLtErrorForecast: Error = {
    title: 'Market Conditions is updating for the day and is currently not available.',
    subtitle: 'Please try again later. You can view an L/T map for past and current timeframes during this update.',
}

export interface McLocationCoordinates {
    lat: number | null;
    lng: number | null;
}

//#endregion